import { computed, inject, Injectable, signal } from '@angular/core';
import { select } from '@ngxs/store';
import { UserPreferencesDispatch } from '@state/user-preferences/user-preferences.dispatch';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';

export type SortDirection = 'asc' | 'desc' | null;
export type SortField = 'packageName' | 'lastUpdated' | null;

const MATTER_PACKAGES_TABLE_ID = 'matter-packages-table';

@Injectable()
export class MatterPackagesViewModel {
	private readonly userPreferencesDispatch = inject(UserPreferencesDispatch);
	private readonly tableSortPreferences = select(
		UserPreferencesState.getTableSortPreferences,
	);

	constructor() {
		this.initializeSortPreferences();
	}

	private initializeSortPreferences(): void {
		const preferences = this.tableSortPreferences()(MATTER_PACKAGES_TABLE_ID);
		this._sortField.set((preferences?.field as SortField) || null);
		this._sortDirection.set(preferences?.direction || null);
	}

	private readonly _sortField = signal<SortField>(null);
	private readonly _sortDirection = signal<SortDirection>(null);

	readonly sortField = this._sortField.asReadonly();
	readonly sortDirection = this._sortDirection.asReadonly();

	readonly sortDirections = computed(() => ({
		packageName:
			this._sortField() === 'packageName' ? this._sortDirection() : null,
		lastUpdated:
			this._sortField() === 'lastUpdated' ? this._sortDirection() : null,
	}));

	readonly sortIcons = computed(() => ({
		packageName: this.getSortIconForField('packageName'),
		lastUpdated: this.getSortIconForField('lastUpdated'),
	}));

	toggleSort(field: SortField): void {
		let direction: SortDirection = 'asc';

		if (this._sortField() === field) {
			if (this._sortDirection() === 'asc') {
				direction = 'desc';
			} else if (this._sortDirection() === 'desc') {
				field = null;
				direction = null;
			}
		}

		this._sortField.set(field);
		this._sortDirection.set(direction);

		this.userPreferencesDispatch.SetTableSortPreferences(
			MATTER_PACKAGES_TABLE_ID,
			{
				field,
				direction,
			},
		);
	}

	private getSortIconForField(field: SortField): string {
		const direction = this.sortDirections()[field];
		if (!direction) return 'selector';
		return direction === 'asc' ? 'chevron-up' : 'chevron-down';
	}
	sortPackages<
		T extends {
			packageName: string;
			lastUpdatedAt?: string | number;
			createdAt?: string | number;
		} & Record<string, unknown>,
	>(packages: T[]): T[] {
		const sortField = this._sortField();
		const sortDirection = this._sortDirection();

		if (!sortField || !sortDirection) {
			return packages;
		}

		return [...packages].sort((a, b) => {
			let comparison = 0;

			switch (sortField) {
				case 'packageName':
					comparison = a.packageName.localeCompare(b.packageName);
					break;
				case 'lastUpdated': {
					const dateA = new Date(a.lastUpdatedAt || a.createdAt).getTime();
					const dateB = new Date(b.lastUpdatedAt || b.createdAt).getTime();
					comparison = dateB - dateA;
					break;
				}
			}

			return sortDirection === 'asc' ? comparison : -comparison;
		});
	}
}
